import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, ClipboardList, CheckCircle2, Users, ArrowRight, Eye, Printer } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const styles = {
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200', draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    archived: 'bg-zinc-100 text-zinc-400 border-zinc-200', complete: 'bg-blue-50 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-50 text-amber-700 border-amber-200', abandoned: 'bg-zinc-100 text-zinc-400 border-zinc-200',
  };
  return <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${styles[status] || ''}`}>{(status || '').replace('_', ' ')}</Badge>;
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get('/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/runs?limit=8').then(r => setRuns(r.data)).catch(() => {});
    if (user?.role === 'admin') {
      api.get('/templates').then(r => setTemplates(r.data.filter(t => t.status === 'published'))).catch(() => {});
    }
  }, [user]);

  const statCards = [
    { label: 'Templates', value: stats.templates, icon: FileText },
    { label: 'Runs this month', value: stats.runs_this_month, icon: ClipboardList },
    { label: 'Completed', value: stats.completed, icon: CheckCircle2 },
    { label: 'Users', value: stats.users, icon: Users },
  ];

  return (
    <div className="p-6" data-testid="dashboard-page">
      <p className="text-sm text-zinc-500 mb-6">Welcome back, {user?.name}</p>
      <div className="grid grid-cols-4 gap-4 mb-8">
        {statCards.map(s => (
          <Card key={s.label} className="rounded-sm border-zinc-200 shadow-none hover:border-zinc-300 transition-colors duration-150" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">{s.label}</span>
                <s.icon size={14} className="text-zinc-300" />
              </div>
              <div className="text-2xl font-bold tracking-tight text-zinc-900">{s.value ?? '-'}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-sm border-zinc-200 shadow-none mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <span className="text-sm font-semibold text-zinc-800">Recent runs</span>
          <Link to="/checklists" className="text-[11px] text-zinc-400 hover:text-zinc-600 font-medium flex items-center gap-1">View all <ArrowRight size={11} /></Link>
        </div>
        {runs.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-zinc-400">No runs yet. <Link to="/checklists/new" className="underline font-medium">Start one</Link></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Project</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Client</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.slice(0, 6).map(run => (
                <TableRow key={run.id}>
                  <TableCell className="px-4 py-2.5 font-medium text-zinc-800">{run.project_fields?.project_name || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">{run.project_fields?.client_name || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5"><StatusBadge status={run.status} /></TableCell>
                  <TableCell className="px-4 py-2.5">
                    <div className="flex gap-1.5">
                      {run.status === 'in_progress' && <Link to={`/checklists/${run.id}/question`} className="text-zinc-400 hover:text-zinc-700"><ArrowRight size={14} /></Link>}
                      {run.status === 'complete' && <>
                        <Link to={`/checklists/${run.id}/complete`} className="text-zinc-400 hover:text-zinc-700"><Eye size={14} /></Link>
                        <Link to={`/checklists/${run.id}/print`} className="text-zinc-400 hover:text-zinc-700"><Printer size={14} /></Link>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {user?.role === 'admin' && templates.length > 0 && (
        <Card className="rounded-sm border-zinc-200 shadow-none">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <span className="text-sm font-semibold text-zinc-800">Published templates</span>
            <Link to="/admin/templates" className="text-[11px] text-zinc-400 hover:text-zinc-600 font-medium flex items-center gap-1">Manage <ArrowRight size={11} /></Link>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Template</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Questions</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Version</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="px-4 py-2.5 font-medium text-zinc-800">{t.title}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">{t.question_count}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">v{t.version}</TableCell>
                  <TableCell className="px-4 py-2.5"><StatusBadge status={t.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
